import React from "react";
import { cn } from "@/lib/utils";

interface Performance {
  date: string;
  performance_value: number;
}

interface CalendarReturnsTableProps {
  performance: Performance[];
}

export function CalendarReturnsTable({ performance }: CalendarReturnsTableProps) {
  if (!(performance?.length || 0)) {
    return (
      <div className="flex items-center gap-4 p-4 bg-muted/10 rounded-lg">
        <div className="w-12 h-12 bg-muted rounded-lg shrink-0" />
        <div>
          <p className="font-medium text-muted-foreground">No performance data available yet</p>
          <p className="text-sm text-muted-foreground">Check back soon for updates</p>
        </div>
      </div>
    );
  }

  // Group performance data by year and month
  const groupedData = performance.reduce((acc, curr) => {
    const date = new Date(curr.date);
    const year = date.getFullYear();
    const month = date.getMonth();
    
    if (!acc[year]) {
      acc[year] = {
        yearTotal: 0,
        months: Array(12).fill(null)
      };
    }
    
    if (acc[year].months[month] === null) {
      acc[year].months[month] = curr.performance_value;
      acc[year].yearTotal += curr.performance_value;
    }
    
    return acc;
  }, {} as Record<number, { yearTotal: number; months: (number | null)[] }>);

  const years = Object.keys(groupedData)
    .map(Number)
    .sort((a, b) => b - a);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="overflow-x-auto -mx-4 md:-mx-6">
      <div className="min-w-[300px] px-4 md:px-6">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left py-2 px-3">Period</th>
              <th className="text-right py-2 px-3">Return</th>
            </tr>
          </thead>
          <tbody>
            {years.map(year => (
              <React.Fragment key={`year-section-${year}`}>
                <tr className="font-medium bg-muted/50">
                  <td className="py-2 px-3">{year}</td>
                  <td className={cn(
                    "text-right py-2 px-3",
                    groupedData[year].yearTotal >= 0 ? "text-green-600" : "text-destructive"
                  )}>
                    {groupedData[year].yearTotal.toFixed(2)}%
                  </td>
                </tr>
                {groupedData[year].months.map((value, index) => (
                  value !== null && (
                    <tr key={`month-${year}-${index}`}>
                      <td className="py-2 px-3 pl-6">{monthNames[index]} {year}</td>
                      <td className={cn(
                        "text-right py-2 px-3",
                        value >= 0 ? "text-green-600" : "text-destructive"
                      )}>
                        {value.toFixed(2)}%
                      </td>
                    </tr>
                  )
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}